import { effect } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { injectable, inject } from 'src/features/ioc';
import { managedSessionLifecycle } from 'src/lib/featureFlags/flags/managedSessionLifecycle';
import type { ReactBindings } from 'src/types/bindings';

export interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
    _axiosInstance?: AxiosInstance;
    _handledRejectedBearerToken?: string;
}

interface QueuedRequest {
    config: RetryableAxiosRequestConfig;
    resolve: (value: any) => void;
    reject: (error: any) => void;
}

const MAX_KNOWN_SESSION_TOKENS = 4;

@injectable()
export class ApiAuthService implements Disposable {
    #disposeSessionEffect: () => void;
    #isRefreshing = false;
    #knownSessionTokens = new Set<string>();
    #log: ReactBindings['Logger'];
    #managedLifecycleEnabled: ReadonlySignal<boolean>;
    #requestQueue: QueuedRequest[] = [];

    constructor(
        @inject('SessionService')
        private readonly sessionService: ReactBindings['SessionService'],
        @inject('FeatureFlagService')
        featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#log = logger.getSubLogger({ name: 'ApiAuthService' }, { component: 'auth' });
        this.#managedLifecycleEnabled = featureFlagService.getSignal(managedSessionLifecycle);
        this.#disposeSessionEffect = effect(() => {
            const accessToken = this.sessionService.accessToken.value;
            if (accessToken) {
                this.#knownSessionTokens.add(accessToken);
                while (this.#knownSessionTokens.size > MAX_KNOWN_SESSION_TOKENS) {
                    const oldestToken = this.#knownSessionTokens.values().next().value;
                    if (oldestToken === undefined) {
                        break;
                    }
                    this.#knownSessionTokens.delete(oldestToken);
                }
            } else {
                this.#knownSessionTokens.clear();
            }

            if (this.sessionService.state.value.phase === 'expired') {
                this.#clearQueue('Auth state cleared');
            }
        });
    }

    /**
     * Handles a request that received a 401 response, potentially refreshing the token
     * and retrying the request.
     *
     * @returns A promise that resolves to the retry response or rejects with the original error
     */
    async handleUnauthorizedRequest(error: AxiosError): Promise<any> {
        // Validate this is a 401 we should handle
        if (!this.#shouldHandleUnauthorized(error)) {
            throw error;
        }

        const originalRequest: RetryableAxiosRequestConfig | undefined = error.config;
        if (!originalRequest) {
            throw error;
        }

        if (!this.#managedLifecycleEnabled.value) {
            if (TRACE) {
                this.#log.trace('Managed session lifecycle disabled for 401 handling');
            }
            throw error;
        }

        const rejectedToken = this.#getBearerToken(originalRequest);
        if (!rejectedToken) {
            // This service may refresh only the managed bearer identity that was
            // actually rejected. Retrying an unauthenticated or differently
            // authenticated request with the session token would change its identity.
            throw error;
        }

        const currentToken = this.sessionService.accessToken.value;
        if (currentToken && rejectedToken !== currentToken) {
            // Only replace a token that this session previously owned. Other
            // bearer contexts (notably masquerade tokens) have independent
            // lifecycles and must not be silently replaced with the user's
            // regular identity.
            if (!this.#knownSessionTokens.has(rejectedToken)) {
                throw error;
            }
        }

        // Bound retry handling to the identity that was actually rejected. The
        // request may next be sent with a replacement token, whose own rejection
        // must remain visible to the session lifecycle.
        originalRequest._handledRejectedBearerToken = rejectedToken;

        // A known historical token must wait for the same replacement as the
        // currently rejected token. Retrying it with the current token here would
        // use the token whose refresh is already in flight.
        if (this.#isRefreshing) {
            return this.#queueRequest(originalRequest);
        }

        if (currentToken && rejectedToken !== currentToken) {
            originalRequest.headers.set('Authorization', `Bearer ${currentToken}`);
            return this.#getAxiosInstance(originalRequest)(originalRequest);
        }

        // Start refresh process
        this.#isRefreshing = true;

        try {
            const sessionState = await this.sessionService.refresh(rejectedToken);
            const token = this.sessionService.accessToken.value;

            if (sessionState.phase === 'authenticated' && token) {
                // Update original request with new token
                originalRequest.headers.set('Authorization', `Bearer ${token}`);

                // Process queued requests with new token
                this.#processQueue(true, token);
                // Token rotation is complete before the retried network request
                // settles. A 401 for the replacement must start a new bounded
                // rejection report instead of being stranded in this queue.
                this.#isRefreshing = false;

                // Retry the original request using the same axios instance
                return this.#getAxiosInstance(originalRequest)(originalRequest);
            } else {
                // Token refresh failed
                this.#processQueue(false);
                throw error;
            }
        } catch (refreshError) {
            this.#log.warn('Token refresh failed', { error: refreshError });
            this.#processQueue(false);
            throw error;
        } finally {
            this.#isRefreshing = false;
        }
    }

    #getBearerToken(config: InternalAxiosRequestConfig): string | undefined {
        const authorization = config.headers.get?.('Authorization') ?? config.headers['Authorization'];
        if (typeof authorization !== 'string' || !authorization.startsWith('Bearer ')) {
            return undefined;
        }

        return authorization.slice('Bearer '.length);
    }

    #shouldHandleUnauthorized(error: AxiosError): boolean {
        // Not a 401
        if (error.response?.status !== 401) {
            return false;
        }

        const config: RetryableAxiosRequestConfig | undefined = error.config;
        if (!config) {
            return false;
        }

        const rejectedToken = this.#getBearerToken(config);
        if (rejectedToken && config._handledRejectedBearerToken === rejectedToken) {
            return false;
        }

        // Skip auth endpoints to prevent loops
        const url = config.url || '';
        if (url.includes('/oauth/') || url.includes('/token') || url.includes('/auth/')) {
            if (TRACE) {
                this.#log.trace('Skipping 401 handling for auth endpoint', { url });
            }
            return false;
        }

        return true;
    }

    #getAxiosInstance(config: RetryableAxiosRequestConfig): AxiosInstance {
        return config._axiosInstance ?? axios;
    }

    #queueRequest(config: RetryableAxiosRequestConfig): Promise<any> {
        return new Promise((resolve, reject) => {
            this.#requestQueue.push({
                config,
                resolve: (updatedConfig) => {
                    // Retry with updated config using the same axios instance
                    resolve(this.#getAxiosInstance(updatedConfig)(updatedConfig));
                },
                reject,
            });
        });
    }

    #processQueue(success: boolean, token?: string): void {
        const queue = [...this.#requestQueue];
        this.#requestQueue = [];

        if (TRACE) {
            this.#log.trace('Processing request queue', {
                queueSize: queue.length,
                success,
            });
        }

        queue.forEach(({ config, resolve, reject }) => {
            if (success && token) {
                // Update config with new token
                config.headers.set('Authorization', `Bearer ${token}`);
                resolve(config);
            } else {
                reject(new Error('Token refresh failed'));
            }
        });
    }

    #clearQueue(reason: string): void {
        const queue = [...this.#requestQueue];
        this.#requestQueue = [];

        if (queue.length > 0) {
            this.#log.debug('Clearing request queue', {
                queueSize: queue.length,
                reason,
            });
        }

        queue.forEach(({ reject }) => {
            reject(new Error(reason));
        });
    }

    [Symbol.dispose](): void {
        this.#disposeSessionEffect();
        this.#clearQueue('ApiAuthService disposed');
    }
}
